/* Product page behaviour:
   - bundle cards set the quantity (card 3 = "3 or more" with a stepper);
     one wood-picker row appears per Kiuas so mixed bundles are possible
     (e.g. 1× Walnut + 1× Birch)
   - totals are computed from data-base-price + the discount percentages
     and shown on the button (and on the 3+ card as the stepper moves)
   - accordions: one open at a time (reference behaviour)
   - add to cart: AJAX (/cart/add.js, multiple line items grouped by
     variant) with button feedback; dispatches `onni:cart:changed` so the
     cart drawer (onni-cart.js) refreshes, opens and syncs the header count;
     the native form post (first wood × quantity) remains as no-JS fallback
   Registry-managed: the product page lives inside the Barba container, so
   this re-runs after every swap; listeners carry the page's abort signal. */
(() => {
  const init = (ctx) => {
  const signal = ctx && ctx.signal;
  const root = document.querySelector('[data-pdp]');
  if (!root) return;
  root.classList.add('is-ready');

  let basePrice = parseInt(root.dataset.basePrice, 10) || 0; // cents
  const pct2 = parseInt(root.dataset.pct2, 10) || 0;
  const pct3 = parseInt(root.dataset.pct3, 10) || 0;

  const idInput = root.querySelector('[data-pdp-id]');
  if (idInput) idInput.disabled = false;
  const qtyInput = root.querySelector('[data-pdp-qty]');
  const atcBtn = root.querySelector('[data-pdp-atc]');
  const form = root.querySelector('[data-pdp-form]');
  const mainPh = root.querySelector('[data-pdp-ph]');
  const mainImg = root.querySelector('.pdp_img.is-main img');
  const unitsWrap = root.querySelector('[data-pdp-units]');
  const unitTemplate = root.querySelector('[data-pdp-unit-template]');
  const stepper = root.querySelector('[data-pdp-stepper]');
  const stepperQty = root.querySelector('[data-pdp-stepper-qty]');
  const price3El = root.querySelector('[data-pdp-price3]');

  let qty = 1;

  /* ---------- money (store currency is EUR) ---------- */
  const money = (cents) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: root.dataset.currency || 'EUR' }).format(cents / 100);

  const discountPct = (q) => (q >= 3 ? pct3 : q === 2 ? pct2 : 0);

  const totalFor = (q) => Math.round(basePrice * q * (100 - discountPct(q)) / 100);
  const selectedSubtotal = () => {
    const selected = unitsWrap ? [...unitsWrap.querySelectorAll('input:checked')] : [];
    return selected.length ? selected.reduce((sum, input) => sum + Number(input.dataset.price || basePrice), 0) : basePrice * qty;
  };
  const selectedTotal = () => Math.round(selectedSubtotal() * (100 - discountPct(qty)) / 100);
  const status = root.querySelector('[data-pdp-status]');
  const mobileBuy = root.querySelector('[data-pdp-mobile-buy]');
  const mobileTotal = root.querySelector('[data-pdp-mobile-total]');
  const mobileLink = root.querySelector('[data-pdp-mobile-link]');

  /* ---------- wood unit rows ---------- */
  const syncUnits = () => {
    if (!unitsWrap || !unitTemplate) return;
    const units = [...unitsWrap.querySelectorAll('[data-pdp-unit]')];

    // add missing rows (cloned from the template, unique radio group names)
    for (let i = units.length; i < qty; i += 1) {
      const clone = unitTemplate.content.firstElementChild.cloneNode(true);
      clone.querySelectorAll('input').forEach((input) => {
        input.name = 'pdp-wood-' + i;
      });
      bindVariantInputs(clone);
      unitsWrap.appendChild(clone);
    }

    // remove extra rows (keep selections of the ones that stay)
    [...unitsWrap.querySelectorAll('[data-pdp-unit]')].forEach((unit, i) => {
      if (i >= qty) unit.remove();
    });

    // labels only make sense with more than one Kiuas
    [...unitsWrap.querySelectorAll('[data-pdp-unit]')].forEach((unit, i) => {
      const label = unit.querySelector('[data-pdp-unit-label]');
      if (!label) return;
      label.textContent = 'Kiuas ' + String(i + 1).padStart(2, '0');
      label.hidden = qty < 2;
    });
  };

  /* ---------- totals ---------- */
  const syncTotals = () => {
    if (qtyInput) qtyInput.value = qty;
    const liveTotal = root.querySelector('[data-pdp-atc-total]');
    if (liveTotal) liveTotal.textContent = money(selectedTotal());
    if (mobileTotal) mobileTotal.textContent = money(selectedTotal());
    const headerPrice = root.querySelector('[data-pdp-price]');
    if (headerPrice) headerPrice.textContent = money(basePrice);
    const price1 = root.querySelector('[data-pdp-price1]');
    const price2 = root.querySelector('[data-pdp-price2]');
    if (price1) price1.textContent = money(basePrice);
    if (price2) price2.innerHTML = money(qty === 2 ? selectedTotal() : totalFor(2)) + ' <s>' + money(qty === 2 ? selectedSubtotal() : basePrice * 2) + '</s>';
    if (price3El && qty >= 3) {
      price3El.innerHTML = money(selectedTotal()) + ' <s>' + money(selectedSubtotal()) + '</s>';
    } else if (price3El) {
      price3El.innerHTML = money(totalFor(3)) + ' <s>' + money(basePrice * 3) + '</s>';
    }
    if (stepperQty) stepperQty.textContent = qty >= 3 ? String(qty) : '3';
  };

  /* ---------- variant inputs (first row drives image/tint + form id) ---------- */
  function bindVariantInputs(scope) {
    scope.querySelectorAll('[data-pdp-variant]').forEach((input) => {
      input.addEventListener('change', () => {
        const firstUnit = unitsWrap ? unitsWrap.querySelector('[data-pdp-unit]') : null;
        if (!firstUnit || !firstUnit.contains(input)) { syncTotals(); return; }
        basePrice = Number(input.dataset.price || basePrice);

        if (idInput) idInput.value = input.value;

        if (mainPh && input.dataset.tint) {
          mainPh.classList.remove('is-walnut', 'is-birch');
          mainPh.classList.add(input.dataset.tint);
        }

        if (mainImg && input.dataset.img) {
          // keep it responsive: swap srcset with src (same sizes), so the
          // browser picks the same candidate warmVariant already fetched
          if (input.dataset.srcset) mainImg.srcset = input.dataset.srcset;
          else mainImg.removeAttribute('srcset');
          mainImg.src = input.dataset.img;
          mainImg.alt = input.dataset.alt || root.dataset.productTitle;
        }
        syncTotals();
        syncGallery(input);
      }, { signal });

      // fetch the variant photo on intent (hover / focus / touch), so the
      // swap on click shows a finished image instead of a blank-then-pop
      const swatch = input.closest('.pdp_swatch') || input;
      ['pointerenter', 'focusin', 'touchstart'].forEach((type) => {
        swatch.addEventListener(type, () => warmVariant(input), { once: true, passive: true, signal });
      });
    });
  }

  function warmVariant(input) {
    if (!mainImg || !input.dataset.img || !window.OnniWarm) return;
    window.OnniWarm.img({
      src: input.dataset.img,
      srcset: input.dataset.srcset,
      sizes: mainImg.getAttribute('sizes')
    });
  }

  function syncGallery(input) {
    const gallery = root.querySelector('.pdp_gallery');
    if (!gallery) return;
    const finish = input.dataset.tint === 'is-birch' ? 'birch' : 'walnut';
    gallery.querySelectorAll('[data-pdp-media-finish]').forEach((item) => {
      item.hidden = item.dataset.pdpMediaFinish !== 'shared' && item.dataset.pdpMediaFinish !== finish;
    });
    gallery.scrollLeft = 0;
  }

  bindVariantInputs(root);
  const initialFinish = root.querySelector('[data-pdp-unit] input:checked');
  if (initialFinish) syncGallery(initialFinish);

  /* ---------- bundle cards + stepper ---------- */
  root.querySelectorAll('[data-pdp-bundle]').forEach((input) => {
    input.addEventListener('change', () => {
      const v = parseInt(input.value, 10);
      qty = v >= 3 ? Math.max(qty, 3) : v;
      if (v < 3) qty = v;
      if (stepper) stepper.hidden = v < 3;
      syncUnits();
      syncTotals();
    }, { signal });
  });

  if (stepper) {
    stepper.querySelectorAll('[data-pdp-step]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const delta = parseInt(btn.dataset.pdpStep, 10);
        qty = Math.min(9, Math.max(3, qty + delta));
        syncUnits();
        syncTotals();
      }, { signal });
    });
  }

  /* ---------- accordions: one open at a time ---------- */
  const accs = [...root.querySelectorAll('[data-pdp-acc]')];
  accs.forEach((acc) => {
    const head = acc.querySelector('[data-pdp-acc-head]');
    if (!head) return;
    head.addEventListener('click', () => {
      const isOpen = acc.classList.contains('open');
      accs.forEach((other) => {
        other.classList.remove('open');
        const h = other.querySelector('[data-pdp-acc-head]');
        if (h) h.setAttribute('aria-expanded', 'false');
      });
      if (!isOpen) {
        acc.classList.add('open');
        head.setAttribute('aria-expanded', 'true');
      }
    }, { signal });
  });

  /* ---------- AJAX add to cart ---------- */
  const collectItems = () => {
    // one line item per variant, aggregated over the unit rows
    const counts = {};
    if (unitsWrap) {
      unitsWrap.querySelectorAll('[data-pdp-unit]').forEach((unit) => {
        const checked = unit.querySelector('input:checked');
        if (checked) counts[checked.value] = (counts[checked.value] || 0) + 1;
      });
    }
    const items = Object.entries(counts).map(([id, quantity]) => ({
      id: Number(id),
      quantity,
    }));
    // no unit rows rendered (single-variant product): fall back to the form
    if (!items.length && idInput) {
      items.push({ id: Number(idInput.value), quantity: qty });
    }
    return items;
  };

  if (form && atcBtn) {
    const label = atcBtn.querySelector('[data-pdp-atc-label]');
    const originalHtml = label ? label.innerHTML : '';

    form.addEventListener('submit', async (event) => {
      if (!window.fetch) return; // native fallback
      event.preventDefault();

      if (atcBtn.disabled) return;
      atcBtn.disabled = true;
      if (status) { status.hidden = true; status.textContent = ''; }
      if (label) label.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: collectItems() }),
          signal,
        });
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          throw new Error(typeof error.description === 'string' ? error.description : 'Could not add your Kiuas. Please try again.');
        }
        if (signal && signal.aborted) return;

        atcBtn.classList.add('is-added');
        if (label) label.textContent = 'Added to cart';
        // cart drawer refreshes itself, opens, and updates the header count
        document.dispatchEvent(new CustomEvent('onni:cart:changed'));

        const resetTimer = setTimeout(() => {
          atcBtn.classList.remove('is-added');
          atcBtn.disabled = false;
          if (label) label.innerHTML = originalHtml;
          syncTotals();
        }, 2000);
        if (signal) signal.addEventListener('abort', () => clearTimeout(resetTimer), { once: true });
      } catch (e) {
        if (signal && signal.aborted) return;
        atcBtn.disabled = false;
        if (label) label.innerHTML = originalHtml;
        syncTotals();
        if (status) { status.hidden = false; status.textContent = e.message || 'Could not add your Kiuas. Please try again.'; }
      }
    }, { signal });
  }

  syncTotals();
  // Only show the mobile anchor while the product is in view and the actual
  // purchase button is offscreen. It never submits an unreviewed selection.
  if (mobileBuy && mobileLink && window.IntersectionObserver) {
    let productVisible = true;
    let buttonVisible = false;
    const updateMobile = () => { mobileBuy.hidden = !productVisible || buttonVisible; };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === root) productVisible = entry.isIntersecting;
        if (entry.target === atcBtn) buttonVisible = entry.isIntersecting;
      });
      updateMobile();
    });
    observer.observe(root);
    if (atcBtn) observer.observe(atcBtn);
    if (signal) signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    mobileLink.addEventListener('click', (event) => {
      const purchase = root.querySelector('.pdp_info');
      if (!purchase || !window.OnniLenis) return;
      event.preventDefault();
      window.OnniLenis.scrollTo(purchase, { offset: -90 });
    }, { signal });
  }
  };

  if (window.Onni && window.Onni.register) {
    window.Onni.register('product-page', init);
  } else {
    init();
  }
})();
